import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { OrganizationService } from '../../services/organization.service';
import { PropertyService } from '../../services/property.service';

@Component({
  selector: 'app-lease-view',
  templateUrl: './lease-view.component.html',
  styleUrls: ['./lease-view.component.scss']
})
export class LeaseViewComponent implements OnInit, OnChanges {
  @Input()
  lease;

  @Input()
  property;

  @Input()
  readOnly = false;

  /** When true, hide the Cancel/Back button (parent dialog already has Close). */
  @Input()
  hideCancel = false;

  users;
  selectedUser = null;
  documents: any[] = [];
  documentsLoading = false;
  documentBusyId: string = null;
  reanalyzeMessage = '';

  @Output()
  onCancel = new EventEmitter<void>();

  @Output()
  onSave = new EventEmitter<void>();

  /** Parent should close any dialog, then navigate to the document viewer. */
  @Output()
  onViewDocument = new EventEmitter<any>();

  possibleDates = [];

  constructor(
    private orgService: OrganizationService,
    private propertyService: PropertyService
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['lease'] && this.lease) {
      this.updateSelectedUserFromLease();
      if (!this.lease.notes) {
        this.lease.notes = this.lease.notes || '';
      }
      this.documents = Array.isArray(this.lease.documents) ? this.lease.documents : [];
      this.loadDocuments();
    }
  }

  ngOnInit() {
    for (var i = 1; i <= 31; i++) {
      this.possibleDates.push(i);
    }
    this.orgService.getOrganization().then(org => {
      this.orgService.getUsers(org).then(users => {
        this.users = users;
        this.updateSelectedUserFromLease();
      });
    });
    this.loadDocuments();
  }

  private propertyId(): string {
    return (this.property && this.property.id)
      || (this.lease && this.lease.propertyId)
      || (this.lease && this.lease.property && this.lease.property.id)
      || null;
  }

  loadDocuments() {
    const propertyId = this.propertyId();
    const leaseId = this.lease && this.lease.id;
    if (!propertyId || !leaseId) {
      return;
    }
    this.documentsLoading = true;
    this.propertyService.getLeaseDocuments(propertyId, leaseId).then((docs: any[]) => {
      this.documents = Array.isArray(docs) ? docs : [];
      this.documentsLoading = false;
    }).catch(() => {
      this.documentsLoading = false;
    });
  }

  viewDocument(doc: any) {
    const propertyId = this.propertyId();
    if (!propertyId || !doc || !doc.id) {
      return;
    }
    this.onViewDocument.emit({
      propertyId,
      documentId: doc.id,
      leaseId: this.lease && this.lease.id,
      fileName: doc.originalFilename || 'lease.pdf'
    });
  }

  downloadDocument(doc: any) {
    const propertyId = this.propertyId();
    if (!propertyId || !doc || !doc.id) {
      return;
    }
    this.documentBusyId = doc.id;
    this.propertyService.downloadLeaseDocument(
      propertyId,
      doc.id,
      doc.originalFilename || 'lease.pdf'
    ).then(() => {
      this.documentBusyId = null;
    }).catch((err) => {
      this.documentBusyId = null;
      console.error('Download failed:', err);
      alert('Failed to download lease document.');
    });
  }

  reanalyzeDocument(doc: any) {
    const propertyId = this.propertyId();
    if (!propertyId || !doc || !doc.id || this.readOnly) {
      return;
    }
    this.documentBusyId = doc.id;
    this.reanalyzeMessage = '';
    this.propertyService.reanalyzeLeaseDocument(propertyId, doc.id).then((res: any) => {
      this.documentBusyId = null;
      const draft = res && res.draft ? res.draft : null;
      if (!draft || !this.lease) {
        this.reanalyzeMessage = 'Re-read finished, but no fields were returned.';
        return;
      }
      if (draft.firstName) this.lease.firstName = draft.firstName;
      if (draft.lastName) this.lease.lastName = draft.lastName;
      if (draft.emailAddress) this.lease.emailAddress = draft.emailAddress;
      if (draft.phoneNumber) this.lease.phoneNumber = draft.phoneNumber;
      if (draft.type) this.lease.type = draft.type;
      if (draft.startDate) this.lease.startDate = draft.startDate;
      if (draft.endDate) this.lease.endDate = draft.endDate;
      if (draft.monthlyCost != null) this.lease.monthlyCost = draft.monthlyCost;
      if (draft.deposit != null) this.lease.deposit = draft.deposit;
      if (draft.invoiceDayOfMonth != null) this.lease.invoiceDayOfMonth = draft.invoiceDayOfMonth;
      if (draft.invoiceDueDayOfMonth != null) this.lease.invoiceDueDayOfMonth = draft.invoiceDueDayOfMonth;
      if (draft.lateFeePercent != null) this.lease.lateFeePercent = draft.lateFeePercent;
      if (draft.lateFeeFrequency != null) this.lease.lateFeeFrequency = draft.lateFeeFrequency;
      if (draft.rollToMonthToMonth != null) this.lease.rollToMonthToMonth = !!draft.rollToMonthToMonth;
      if (draft.notes) this.lease.notes = draft.notes;
      this.updateSelectedUserFromLease();
      this.loadDocuments();
      this.reanalyzeMessage = 'Form updated from the document. Review and Save to keep changes.';
    }).catch((err) => {
      this.documentBusyId = null;
      console.error('Reanalyze failed:', err);
      const msg = (err.error && err.error.error) ? err.error.error : 'Failed to re-read lease document.';
      alert(msg);
    });
  }

  onUploadDocument(event: any) {
    const input = event && event.target ? event.target : null;
    const file: File = input && input.files && input.files[0] ? input.files[0] : null;
    if (input) {
      input.value = '';
    }
    const propertyId = this.propertyId();
    const leaseId = this.lease && this.lease.id;
    if (!file || !propertyId || !leaseId || this.readOnly) {
      return;
    }
    this.documentsLoading = true;
    this.propertyService.uploadLeaseDocument(propertyId, leaseId, file).then(() => {
      this.documentsLoading = false;
      this.loadDocuments();
    }).catch((err) => {
      this.documentsLoading = false;
      console.error('Upload failed:', err);
      const msg = (err.error && err.error.error) ? err.error.error : 'Failed to upload lease document.';
      alert(msg);
    });
  }

  private updateSelectedUserFromLease() {
    if (this.users && this.lease && this.lease.emailAddress) {
      const match = this.users.find(u => u.emailAddress === this.lease.emailAddress);
      this.selectedUser = match ? match : null;
    }
  }

  cancelAction() {
    if (this.onCancel) {
      this.onCancel.emit();
    }
  }

  saveAction() {
    if (this.readOnly) {
      return;
    }
    if (this.onSave) {
      this.onSave.emit();
    }
  }

  onUserChange() {
    if (this.readOnly || !this.lease) {
      return;
    }
    if (this.selectedUser) {
      this.lease.firstName = this.selectedUser.firstName;
      this.lease.lastName = this.selectedUser.lastName;
      this.lease.emailAddress = this.selectedUser.emailAddress;
    } else {
      this.lease.firstName = '';
      this.lease.lastName = '';
      this.lease.emailAddress = '';
    }
  }

  onLeaseTypeChange() {
    if (this.readOnly) {
      return;
    }
    if (this.lease && this.lease.type === 'M2M') {
      this.lease.startDate = null;
      this.lease.endDate = null;
    }
  }
}
